/**
 * /analytics — marketing analytics (Meta Pixel · Microsoft Clarity · GA4 ·
 * Instagram).
 *
 * Sourced from `GET /api/analytics`: the latest snapshot per source from
 * `website_analytics_snapshots` (written daily by the
 * `website_analytics_daily` cron saga) plus the cross-row Clarity trend.
 * Meta + GA4 trends come from the latest snapshot's own 28-day daily
 * series; Clarity's API window is only 1..3 days, so its trend is
 * assembled across the stored days. A source whose last pull failed
 * renders dark with its stable error code. Read-only, staff-facing.
 *
 * 2026-08-06 (migration 155) — the Instagram section reads
 * `instagram_insights_snapshots`, written by the SEPARATE
 * `instagram_insights_daily` cron. Separate table and cron because the two
 * gate on different config (an L2 `website_analytics` block vs the Instagram
 * connector + its secrets); the PAGE is where they unify, which is where it
 * matters to a human.
 */

import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  api,
  ApiError,
  type PurchaseSourceRow,
  type WebsiteAnalyticsLatestSnapshot,
  type WebsiteAnalyticsResponse,
} from "../lib/api";
import {
  ACTIVE_DOT,
  AXIS_STROKE,
  AXIS_STYLE,
  BAR_CURSOR,
  CHART_HEIGHT,
  GRID_STROKE,
  LINE_CURSOR,
  TOOLTIP_CONTENT_STYLE,
} from "../lib/chart-theme";
import { fmtMoney } from "../lib/format-money";
import { collapseGa4Events, orderGa4Events } from "../lib/ga4-event-rows";
import {
  isMetaConversionEvent,
  sumMetaConversions,
} from "../lib/meta-conversion-events";
import { PANEL_VISITOR_CLASSES } from "@layer/canonical-data/booking-panel-steps";
import {
  defaultPanelStepFlow,
  panelStepFlows,
  panelStepTable,
  type PanelStepWindow,
} from "../lib/panel-step-table";

type Rec = Record<string, unknown>;

const rec = (v: unknown): Rec =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Rec) : {};
const arr = (v: unknown): ReadonlyArray<unknown> => (Array.isArray(v) ? v : []);
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

const fmtInt = (v: number | null): string =>
  v === null ? "—" : v.toLocaleString("en-GB");

/**
 * Migration 205 — the human name for a purchase-source row. `meta` says
 * "Facebook / Instagram", not "Meta ads": the click id that puts a row here
 * rides organic links too, so the label claims only what the data proves.
 */
function purchaseSourceLabel(row: PurchaseSourceRow): string {
  const tagged =
    row.source === null
      ? null
      : row.medium === null
        ? row.source
        : `${row.source} / ${row.medium}`;
  switch (row.channel) {
    case "google_ads":
      return "Google Ads";
    case "meta":
      return "Facebook / Instagram";
    case "tagged":
      return tagged ?? "Tagged link";
    case "referral":
      return row.source === null ? "Referral" : `Referral · ${row.source}`;
    case "direct":
      return "Direct or unknown";
  }
}

/** `2026-07-24` → `24 Jul` (chart tick / table label). */
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Relative freshness of the pixel's last_fired_time (Meta's alive signal). */
function relativeTime(isoish: string): string {
  const d = new Date(isoish);
  if (Number.isNaN(d.getTime())) return isoish;
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (24 * 60))}d ago`;
}

/** A failed pull stores `{error: <stable code>}` as the day's payload. */
function snapshotError(
  snap: WebsiteAnalyticsLatestSnapshot | null,
): string | null {
  return snap === null ? null : str(snap.payload["error"]);
}

function SourceState(props: {
  readonly snap: WebsiteAnalyticsLatestSnapshot | null;
  readonly name: string;
}): JSX.Element | null {
  const err = snapshotError(props.snap);
  if (props.snap === null) {
    return (
      <p className="empty">
        No {props.name} snapshot yet — the <code>website_analytics_daily</code>{" "}
        cron runs at 05:45 UTC (or invoke it from Inngest / the Status page).
      </p>
    );
  }
  if (err !== null) {
    return (
      <p className="member-note">
        <span className="tag tag-danger">{err}</span>{" "}
        <span className="subtle">
          last pull failed on {props.snap.captured_on} — check the secret /
          vendor and re-invoke the cron.
        </span>
      </p>
    );
  }
  return null;
}

export function Analytics(): JSX.Element {
  const [data, setData] = useState<WebsiteAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllEvents, setShowAllEvents] = useState<boolean>(false);
  // The booking-panel steps table: which flow and which window it shows. Null
  // means "not chosen yet", which resolves to the default flow once data is in.
  const [panelStepFlow, setPanelStepFlow] = useState<string | null>(null);
  const [panelStepWindow, setPanelStepWindow] = useState<PanelStepWindow>(7);

  useEffect(() => {
    let cancelled = false;
    api
      .analytics()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? e.code ?? `http_${e.status}`
              : (e as Error).message,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error !== null)
    return <p className="empty">Couldn&apos;t load analytics ({error}).</p>;
  if (data === null) return <p className="empty">Loading analytics…</p>;

  const ga4 = data.latest.ga4;
  const meta = data.latest.meta_pixel;
  const clarity = data.latest.clarity;
  const funnel = data.checkout_funnel;
  const sources = data.purchase_sources;
  const panelSteps = data.panel_steps;
  const panelFlows = panelStepFlows(panelSteps.rows);
  // The chosen flow falls back to the default whenever it holds no count, so
  // a refresh that drops a flow from the window cannot leave an empty table.
  const panelFlow = panelFlows.some((f) => f.flow === panelStepFlow)
    ? panelStepFlow
    : defaultPanelStepFlow(panelFlows);
  const panelRows =
    panelFlow === null ? [] : panelStepTable(panelSteps.rows, panelFlow, panelStepWindow);
  const ga4Ok = ga4 !== null && snapshotError(ga4) === null;
  const metaOk = meta !== null && snapshotError(meta) === null;
  const clarityOk = clarity !== null && snapshotError(clarity) === null;

  // --- GA4 views -----------------------------------------------------------
  const ga4Totals = rec(ga4?.payload["totals"]);
  const ga4Daily = arr(ga4?.payload["daily"]).map(rec);
  const ga4Channels = arr(ga4?.payload["channels"]).map(rec);
  const ga4Pages = arr(ga4?.payload["pages"]).map(rec);
  const ga4Events = orderGa4Events(
    arr(ga4?.payload["events"])
      .map(rec)
      .map((e) => ({
        event: str(e["event"]) ?? "—",
        count: num(e["count"]),
        keyEvent: (num(e["key_events"]) ?? 0) > 0,
      })),
  );
  const ga4EventsShown = showAllEvents
    ? ga4Events
    : collapseGa4Events(ga4Events);
  const ga4Hostnames = arr(ga4?.payload["hostnames"]).map(String);
  const ga4Chart = ga4Daily.map((d) => ({
    date: shortDate(str(d["date"]) ?? ""),
    sessions: num(d["sessions"]) ?? 0,
    users: num(d["users"]) ?? 0,
  }));

  // --- Meta Pixel views ----------------------------------------------------
  const metaEvents = arr(meta?.payload["events"]).map(rec);
  const metaDaily = arr(meta?.payload["daily"]).map(rec);
  // The chart counts conversions only; the table below lists every event
  // type. See `lib/meta-conversion-events.ts` for which names count.
  const metaChart = metaDaily.map((d) => ({
    date: shortDate(str(d["date"]) ?? ""),
    conversions: sumMetaConversions(rec(d["events"])),
  }));
  const metaConversionsTotal = metaChart.reduce(
    (sum, d) => sum + d.conversions,
    0,
  );
  const metaLastFired = str(meta?.payload["last_fired_time"]);
  // Non-null when the token could read the pixel's identity but not its
  // `/stats` edge — counts are UNAVAILABLE (not zero) until the token gains
  // stats access on the pixel's Business.
  const metaStatsError = str(meta?.payload["stats_error"]);

  // --- Clarity views -------------------------------------------------------
  const claritySummary = rec(clarity?.payload["summary"]);
  const clarityPages = arr(clarity?.payload["pages"]).map(rec);
  const clarityDevices = arr(clarity?.payload["devices"]).map(rec);
  const claritySources = arr(clarity?.payload["sources"]).map(rec);
  const clarityChart = data.clarity_trend.map((p) => ({
    date: shortDate(p.captured_on),
    sessions: p.sessions ?? 0,
    rage: p.rage_clicks ?? 0,
  }));
  const frictionClicks =
    (num(claritySummary["rage_clicks"]) ?? 0) +
    (num(claritySummary["dead_clicks"]) ?? 0);
  const scrollDepth = num(claritySummary["avg_scroll_depth"]);

  // --- Instagram views (migration 155) -------------------------------------
  const ig = data.instagram;
  const igError = ig === null ? null : str(ig.payload["error"]);
  const igAccount = rec(ig?.payload["account"]);
  const igDay = rec(ig?.payload["day"]);
  const igUsername = str(igAccount["username"]);
  const igFollowers = num(igAccount["followers_count"]);
  const igReachBreakdown =
    ig === null || ig.payload["reach_breakdown"] === null
      ? null
      : rec(ig.payload["reach_breakdown"]);
  const igTopMedia = arr(ig?.payload["top_media"]).map(rec);
  // 2026-08-06 (migration 156) — stories come off their OWN response field,
  // not out of the snapshot payload, because they are keyed by story and
  // outlive any single day's snapshot.
  const igStories = data.instagram_stories ?? [];
  // Meta's own bucket label — the honest answer to "which day is this?", since
  // captured_on is the day the CRON ran and IG's buckets close at 07:00Z.
  const igDayEnd = str(ig?.payload["day_end_time"]);
  const igDayLabel = igDayEnd === null ? null : shortDate(igDayEnd.slice(0, 10));
  // Both charts come from the snapshot's OWN 30-day series, not from the
  // cross-row trend — one snapshot already carries a month, so the charts are
  // full on day one rather than growing a point per day.
  const igSeries = rec(ig?.payload["series"]);
  const igReachChart = arr(igSeries["reach"]).map(rec).map((p) => ({
    date: shortDate(str(p["end_time"])?.slice(0, 10) ?? ""),
    reach: num(p["value"]) ?? 0,
  }));
  const igFollowerChart = arr(igSeries["follower_count"]).map(rec).map((p) => ({
    date: shortDate(str(p["end_time"])?.slice(0, 10) ?? ""),
    followers: num(p["value"]) ?? 0,
  }));

  return (
    <div>
      <h1 className="page-title">Analytics</h1>
      <p className="member-note">
        Website analytics for{" "}
        <strong>{ga4Hostnames.join(", ") || "the public site"}</strong> — Meta
        Pixel, Microsoft Clarity, and GA4, snapshotted daily by the{" "}
        <code>website_analytics_daily</code> cron.
        {ga4 !== null || meta !== null || clarity !== null ? (
          <span className="subtle">
            {" "}
            · latest snapshot{" "}
            {[ga4, meta, clarity]
              .filter((s): s is WebsiteAnalyticsLatestSnapshot => s !== null)
              .map((s) => s.captured_on)
              .sort()
              .at(-1)}
          </span>
        ) : null}
      </p>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Sessions · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["sessions"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Users · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["users"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Key events · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["key_events"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Pixel events · 28d</div>
          <div className="value">
            {metaOk && metaStatsError === null
              ? fmtInt(num(meta.payload["total_events"]))
              : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Pixel last fired</div>
          <div className="value">
            {metaOk && metaLastFired !== null
              ? relativeTime(metaLastFired)
              : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Friction clicks · 3d (Clarity)</div>
          <div className="value">{clarityOk ? fmtInt(frictionClicks) : "—"}</div>
        </div>
      </div>

      <section className="panel">
        <h2>Checkout funnel — last {funnel.window_days} days</h2>
        <p className="member-note">
          Clavis&apos;s own <code>purchase_attempts</code> substrate — every
          PaymentIntent / Checkout Session the website (or a chat channel)
          minted, and where it ended up. Swept every 15 minutes; alerts + recovery
          emails ride the same rows.
        </p>
        {funnel.started === 0 &&
        funnel.succeeded === 0 &&
        funnel.failed === 0 &&
        funnel.abandoned === 0 ? (
          <p className="empty">
            No purchase attempts captured in the window yet — attempts appear
            once the Stripe webhook subscribes to the{" "}
            <code>payment_intent.*</code> / <code>checkout.session.*</code>{" "}
            events.
          </p>
        ) : (
          <>
            <div className="stat-grid">
              <div className="stat-card">
                <div className="label">Started</div>
                <div className="value">{fmtInt(funnel.started)}</div>
              </div>
              <div className="stat-card">
                <div className="label">Paid</div>
                <div className="value">
                  {fmtInt(funnel.succeeded)}
                  {funnel.started > 0 ? (
                    <span className="subtle">
                      {" "}
                      ({Math.round((funnel.succeeded / funnel.started) * 100)}%)
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Failed</div>
                <div className="value">{fmtInt(funnel.failed)}</div>
              </div>
              <div className="stat-card">
                <div className="label">Abandoned</div>
                <div className="value">{fmtInt(funnel.abandoned)}</div>
              </div>
              <div className="stat-card">
                <div className="label">Lost</div>
                <div className="value">
                  {/* One figure per currency — never summed across them. */}
                  {funnel.lost_by_currency.length === 0
                    ? "0"
                    : funnel.lost_by_currency
                        .map((l) => fmtMoney(l.amount_minor, l.currency))
                        .join(" + ")}
                </div>
              </div>
            </div>
            {funnel.top_lost_offers.length > 0 ? (
              <div className="analytics-tables">
                <div>
                  <h3>Most abandoned</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Offer</th>
                        <th>Lost attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.top_lost_offers.map((offer) => (
                        <tr key={offer.name}>
                          <td>{offer.name}</td>
                          <td>{fmtInt(offer.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="panel">
        <h2>Where buyers came from — last {sources.window_days} days</h2>
        <p className="member-note">
          Website package checkouts by the ad or link that brought the buyer
          in, read from the same <code>purchase_attempts</code> rows. The site
          sends what the buyer&apos;s browser stored (UTM tags, ad click,
          landing page, referrer) when checkout opens. A campaign name shows
          only when the ad&apos;s link carries a <code>utm_campaign</code>{" "}
          tag; &ldquo;Facebook / Instagram&rdquo; includes organic posts; and
          &ldquo;Direct or unknown&rdquo; includes every checkout from before
          the site started sending this. Of the paid checkouts, &ldquo;New&rdquo;
          is the buyer&apos;s first purchase and &ldquo;Returning&rdquo; a
          buyer who had bought before. On an ad row, &ldquo;Found by ad&rdquo;
          counts the new buyers whose ad click came before they had an account:
          the ad found them, rather than reaching someone already known.
        </p>
        {sources.rows.length === 0 ? (
          <p className="empty">
            No website checkouts in the last {sources.window_days} days.
          </p>
        ) : (
          // A scroll box of its own, not `.analytics-tables`: the columns do
          // not fit a phone, and without one the Revenue column is clipped
          // with nothing to scroll (measured at 390px, 2026-10-01).
          <div className="analytics-table-scroll">
            <table className="data-table table-cards">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Campaign</th>
                  <th>Checkouts started</th>
                  <th>Paid</th>
                  <th>New</th>
                  <th>Found by ad</th>
                  <th>Returning</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {sources.rows.map((row) => (
                  <tr
                    key={[
                      row.channel,
                      row.source ?? "",
                      row.medium ?? "",
                      row.campaign ?? "",
                    ].join("|")}
                  >
                    <td className="table-card-title">{purchaseSourceLabel(row)}</td>
                    <td data-label="Campaign">{row.campaign ?? "—"}</td>
                    <td data-label="Checkouts started">{fmtInt(row.started)}</td>
                    <td data-label="Paid">{fmtInt(row.paid)}</td>
                    <td data-label="New">{fmtInt(row.paid_new)}</td>
                    {/* Only an ad row carries the click time "found" is judged by. */}
                    <td data-label="Found by ad">
                      {fmtInt(
                        row.channel === "google_ads" || row.channel === "meta"
                          ? row.paid_found_by_ad
                          : null,
                      )}
                    </td>
                    <td data-label="Returning">{fmtInt(row.paid_returning)}</td>
                    <td data-label="Revenue">£{(row.paid_amount_minor / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <h2>Booking panel steps — last {panelStepWindow} days</h2>
        <p className="member-note">
          How often each step of the booking panel was reached, counted by
          Clavis itself. It includes visitors who never answered the cookie
          banner, whom GA4, the pixel and Clarity cannot see. A visitor who
          tapped Reject is not counted. A screen counts every time it is
          shown. An indented row counts at most once per opening of the
          panel, so compare indented rows with each other.
          {panelSteps.first_day !== null
            ? ` Counting began on ${shortDate(panelSteps.first_day)}.`
            : ""}
        </p>
        {panelFlow === null ? (
          <p className="empty">No booking panel steps counted yet.</p>
        ) : (
          <>
            <div className="panel-step-controls">
              <label>
                Flow{" "}
                <select
                  value={panelFlow}
                  onChange={(e) => setPanelStepFlow(e.target.value)}
                >
                  {panelFlows.map((f) => (
                    <option key={f.flow} value={f.flow}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Window{" "}
                <select
                  value={panelStepWindow}
                  onChange={(e) =>
                    setPanelStepWindow(e.target.value === "28" ? 28 : 7)
                  }
                >
                  <option value="7">Last 7 days</option>
                  <option value="28">Last 28 days</option>
                </select>
              </label>
            </div>
            <div className="analytics-table-scroll">
              <table className="data-table table-cards">
                <thead>
                  <tr>
                    <th>Step</th>
                    {PANEL_VISITOR_CLASSES.map((c) => (
                      <th key={c.visitorClass}>{c.label}</th>
                    ))}
                    <th>All</th>
                  </tr>
                </thead>
                <tbody>
                  {panelRows.map((row) => (
                    <tr key={row.step}>
                      <td
                        className={
                          row.kind === "moment"
                            ? "panel-step-moment table-card-title"
                            : "table-card-title"
                        }
                      >
                        {row.label}
                      </td>
                      {PANEL_VISITOR_CLASSES.map((c) => (
                        <td key={c.visitorClass} data-label={c.label}>
                          {fmtInt(row.counts[c.visitorClass])}
                        </td>
                      ))}
                      <td data-label="All">{fmtInt(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="panel">
        <h2>Traffic — GA4</h2>
        <SourceState snap={ga4} name="GA4" />
        {ga4Ok && ga4Chart.length === 0 ? (
          <p className="member-note subtle">
            No sessions recorded for {ga4Hostnames.join(", ")} in the last{" "}
            {num(ga4.payload["window_days"]) ?? 28} days yet — GA4 processes
            new-site data with a lag, and this property is scoped to those
            hostnames only. Numbers fill in as traffic accrues.
          </p>
        ) : null}
        {ga4Ok && ga4Chart.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={ga4Chart} margin={{ top: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
                <XAxis dataKey="date" tick={AXIS_STYLE} stroke={AXIS_STROKE} minTickGap={24} />
                <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} allowDecimals={false} width={36} />
                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={LINE_CURSOR} />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="sessions"
                  stroke="var(--c-signal-line-53)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={ACTIVE_DOT}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="users"
                  stroke="var(--c-ok-line-55)"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={ACTIVE_DOT}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="analytics-tables">
              <div>
                <h3>Channels</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Sessions</th>
                      <th>Users</th>
                      <th>Key events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Channels.map((c, i) => (
                      <tr key={i}>
                        <td>{str(c["channel"]) ?? "—"}</td>
                        <td>{fmtInt(num(c["sessions"]))}</td>
                        <td>{fmtInt(num(c["users"]))}</td>
                        <td>{fmtInt(num(c["key_events"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Top pages</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th>Views</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Pages.slice(0, 10).map((p, i) => (
                      <tr key={i}>
                        <td>{str(p["path"]) ?? "—"}</td>
                        <td>{fmtInt(num(p["views"]))}</td>
                        <td>{fmtInt(num(p["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Events</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4EventsShown.map((e, i) => (
                      <tr key={i}>
                        <td>
                          {e.event}
                          {e.keyEvent ? (
                            <>
                              {" "}
                              <span className="tag">key event</span>
                            </>
                          ) : null}
                        </td>
                        <td>{fmtInt(e.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ga4Events.length > collapseGa4Events(ga4Events).length ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setShowAllEvents((v) => !v)}
                  >
                    {showAllEvents
                      ? "Show fewer"
                      : `Show all ${ga4Events.length} events`}
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </section>

      <section className="panel">
        <h2>Conversion events — Meta Pixel</h2>
        <SourceState snap={meta} name="Meta Pixel" />
        {metaOk && metaStatsError !== null ? (
          <p className="member-note">
            <span className="tag tag-danger">{metaStatsError}</span>{" "}
            <span className="subtle">
              the pixel is alive (identity + last-fired readable) but the
              token can&apos;t read event counts — it needs stats access on
              the pixel&apos;s Business (regenerate with ads_read and update
              the meta.ads_access_token secret).
            </span>
          </p>
        ) : null}
        {metaOk && metaStatsError === null ? (
          <>
            {metaChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={metaChart} margin={{ top: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
                  <XAxis dataKey="date" tick={AXIS_STYLE} stroke={AXIS_STROKE} minTickGap={24} />
                  <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} allowDecimals={false} width={36} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={BAR_CURSOR} />
                  <Bar isAnimationActive={false} dataKey="conversions" fill="var(--c-signal-fill-53)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
            {metaChart.length > 0 ? (
              <p className="member-note">
                <span className="subtle">
                  The bars count Meta&apos;s standard conversion events:{" "}
                  <strong>{fmtInt(metaConversionsTotal)}</strong> in the{" "}
                  {metaChart.length} days charted. Page views, content views
                  and the site&apos;s own events are in the table below, not
                  in the bars.
                </span>
              </p>
            ) : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>Count · 28d</th>
                </tr>
              </thead>
              <tbody>
                {metaEvents.map((e, i) => (
                  <tr key={i}>
                    <td>
                      {str(e["event"]) ?? "—"}
                      {isMetaConversionEvent(str(e["event"]) ?? "") ? (
                        <>
                          {" "}
                          <span className="tag">conversion</span>
                        </>
                      ) : null}
                    </td>
                    <td>{fmtInt(num(e["count"]))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </section>

      <section className="panel">
        <h2>Behavior — Microsoft Clarity</h2>
        <SourceState snap={clarity} name="Clarity" />
        {clarityOk ? (
          <>
            <p className="member-note">
              Last {num(clarity.payload["num_days"]) ?? 3} days:{" "}
              <strong>{fmtInt(num(claritySummary["sessions"]))}</strong>{" "}
              sessions ·{" "}
              <strong>{fmtInt(num(claritySummary["distinct_users"]))}</strong>{" "}
              users · <strong>{fmtInt(num(claritySummary["rage_clicks"]))}</strong>{" "}
              rage / <strong>{fmtInt(num(claritySummary["dead_clicks"]))}</strong>{" "}
              dead clicks
              {scrollDepth !== null ? (
                <>
                  {" "}
                  · <strong>{scrollDepth.toFixed(0)}%</strong> avg scroll depth
                </>
              ) : null}
            </p>
            {clarityChart.length > 1 ? (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart data={clarityChart} margin={{ top: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
                  <XAxis dataKey="date" tick={AXIS_STYLE} stroke={AXIS_STROKE} minTickGap={24} />
                  <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} allowDecimals={false} width={36} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={LINE_CURSOR} />
                  <Line
                  isAnimationActive={false}
                    type="monotone"
                    dataKey="sessions"
                    stroke="var(--c-signal-line-53)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={ACTIVE_DOT}
                  />
                  <Line
                  isAnimationActive={false}
                    type="monotone"
                    dataKey="rage"
                    stroke="var(--c-danger-line-41)"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={ACTIVE_DOT}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="member-note subtle">
                The trend chart fills in as daily snapshots accumulate
                (Clarity&apos;s API covers only the last 3 days per pull).
              </p>
            )}
            <div className="analytics-tables">
              <div>
                <h3>Pages</h3>
                <table className="data-table table-cards">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Sessions</th>
                      <th>Rage</th>
                      <th>Dead</th>
                      <th>Scroll</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clarityPages.slice(0, 10).map((p, i) => {
                      const depth = num(p["avg_scroll_depth"]);
                      return (
                        <tr key={i}>
                          <td className="analytics-url table-card-title">
                            {(str(p["url"]) ?? "—").replace(
                              /^https?:\/\/[^/]+/,
                              "",
                            ) || "/"}
                          </td>
                          <td data-label="Sessions">{fmtInt(num(p["sessions"]))}</td>
                          <td data-label="Rage">{fmtInt(num(p["rage_clicks"]))}</td>
                          <td data-label="Dead">{fmtInt(num(p["dead_clicks"]))}</td>
                          <td data-label="Scroll">{depth === null ? "—" : `${depth.toFixed(0)}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Devices</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clarityDevices.map((d, i) => (
                      <tr key={i}>
                        <td>{str(d["device"]) ?? "—"}</td>
                        <td>{fmtInt(num(d["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Sources</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claritySources.map((s, i) => (
                      <tr key={i}>
                        <td>{str(s["source"]) ?? "—"}</td>
                        <td>{fmtInt(num(s["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </section>

      <section className="panel">
        {/*
          When the pull failed (or has never run) the account handle is simply
          unknown, and the old `?? "account"` fallback rendered "Instagram —
          @account" — which reads as a real handle rather than an absent one.
          Surfaced by the §8 browser walk, the first to view this panel in its
          error state. Omit the handle instead of inventing one.
        */}
        <h2>
          Instagram
          {igUsername === null || igUsername === "" ? "" : ` — @${igUsername}`}
        </h2>
        {ig === null ? (
          <p className="empty">
            No Instagram snapshot yet — the{" "}
            <code>instagram_insights_daily</code> cron runs at 05:50 and 17:50
            UTC (or invoke it from Inngest / the Status page).
          </p>
        ) : igError !== null ? (
          <p className="member-note">
            <span className="tag tag-danger">{igError}</span>{" "}
            <span className="subtle">
              last pull failed on {ig.captured_on} — check the
              instagram.access_token / instagram.business_account_id secrets
              and re-invoke the cron.
            </span>
          </p>
        ) : (
          <>
            <p className="member-note">
              <strong>{fmtInt(igFollowers)}</strong> followers ·{" "}
              <strong>{fmtInt(igAccount["media_count"] as number | null)}</strong>{" "}
              posts
              {igDayLabel !== null ? (
                <span className="subtle">
                  {" "}
                  · day figures cover the bucket ending {igDayLabel}
                </span>
              ) : null}
            </p>

            <div className="stat-grid">
              <div className="stat-card">
                <div className="label">Reach · 1d</div>
                <div className="value">{fmtInt(num(igDay["reach"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Views · 1d</div>
                <div className="value">{fmtInt(num(igDay["views"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Interactions · 1d</div>
                <div className="value">
                  {fmtInt(num(igDay["total_interactions"]))}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Accounts engaged · 1d</div>
                <div className="value">
                  {fmtInt(num(igDay["accounts_engaged"]))}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Profile views · 1d</div>
                <div className="value">{fmtInt(num(igDay["profile_views"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Link taps · 1d</div>
                <div className="value">
                  {fmtInt(
                    (num(igDay["website_clicks"]) ?? 0) +
                      (num(igDay["profile_links_taps"]) ?? 0),
                  )}
                </div>
              </div>
            </div>

            {igReachBreakdown !== null ? (
              <p className="member-note">
                Reach split:{" "}
                <strong>{fmtInt(num(igReachBreakdown["follower"]))}</strong>{" "}
                followers ·{" "}
                <strong>{fmtInt(num(igReachBreakdown["non_follower"]))}</strong>{" "}
                non-followers
                <span className="subtle">
                  {" "}
                  — the non-follower share is the discovery signal.
                </span>
              </p>
            ) : null}

            {igReachChart.length > 0 ? (
              <>
                <h3>Daily reach · {igReachChart.length}d</h3>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <LineChart data={igReachChart} margin={{ top: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
                    <XAxis dataKey="date" tick={AXIS_STYLE} stroke={AXIS_STROKE} minTickGap={24} />
                    <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} allowDecimals={false} width={44} />
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={LINE_CURSOR} />
                    <Line
                      isAnimationActive={false}
                      type="monotone"
                      dataKey="reach"
                      stroke="var(--c-pink-line-46)"
                      dot={false}
                      activeDot={ACTIVE_DOT}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : null}

            {igFollowerChart.length > 0 ? (
              <>
                <h3>New followers per day</h3>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart
                    data={igFollowerChart}
                    margin={{ top: 8, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
                    <XAxis dataKey="date" tick={AXIS_STYLE} stroke={AXIS_STROKE} minTickGap={24} />
                    <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} allowDecimals={false} width={36} />
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={BAR_CURSOR} />
                    <Bar
                      isAnimationActive={false}
                      dataKey="followers"
                      fill="var(--c-pink-fill-46)"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : null}

            {igTopMedia.length > 0 ? (
              <>
                <h3>Recent posts</h3>
                <table className="data-table table-cards">
                  <thead>
                    <tr>
                      <th>Posted</th>
                      <th>Type</th>
                      <th>Reach</th>
                      <th>Views</th>
                      <th>Likes</th>
                      <th>Comments</th>
                      <th>Saved</th>
                      <th>Shares</th>
                      <th>Follows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {igTopMedia.map((m, i) => {
                      const metrics = rec(m["metrics"]);
                      const permalink = str(m["permalink"]);
                      const ts = str(m["timestamp"]);
                      return (
                        <tr key={i}>
                          <td className="table-card-title">
                            {permalink !== null ? (
                              <a
                                href={permalink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {ts === null ? "—" : shortDate(ts.slice(0, 10))}
                              </a>
                            ) : ts === null ? (
                              "—"
                            ) : (
                              shortDate(ts.slice(0, 10))
                            )}
                          </td>
                          <td data-label="Type">
                            {str(m["media_product_type"]) ??
                              str(m["media_type"]) ??
                              "—"}
                          </td>
                          <td data-label="Reach">{fmtInt(num(metrics["reach"]))}</td>
                          <td data-label="Views">{fmtInt(num(metrics["views"]))}</td>
                          {/* Inline list counts are the fallback: they survive
                              even when a post's own insights call failed. */}
                          <td data-label="Likes">
                            {fmtInt(
                              num(metrics["likes"]) ??
                                num(m["like_count"]),
                            )}
                          </td>
                          <td data-label="Comments">
                            {fmtInt(
                              num(metrics["comments"]) ??
                                num(m["comments_count"]),
                            )}
                          </td>
                          <td data-label="Saved">{fmtInt(num(metrics["saved"]))}</td>
                          <td data-label="Shares">{fmtInt(num(metrics["shares"]))}</td>
                          <td data-label="Follows">{fmtInt(num(metrics["follows"]))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : null}

          </>
        )}

        {/* 2026-08-06 (migration 156) — stories. Deliberately OUTSIDE the
            ig === null / igError ternary above: it reads
            `instagram_story_insights`, not `ig.payload`, so a failed account
            pull (or a day with no snapshot at all) must not hide stories that
            were already recorded. They are separate data with a separate
            failure mode. */}
        {igStories.length > 0 ? (
              <>
                <h3>Stories · last {igStories.length}</h3>
                <p className="muted">
                  Stories expire after 24h, so these are pulled every 12h and
                  each row keeps its most complete reading. “Seen” is how many
                  runs caught the story alive — a story seen once means the
                  poll only just managed it.
                </p>
                <table className="data-table table-cards">
                  <thead>
                    <tr>
                      <th>Posted</th>
                      <th>Reach</th>
                      <th>Views</th>
                      <th>Replies</th>
                      <th>Shares</th>
                      <th>Navigation</th>
                      <th>Follows</th>
                      <th>Profile visits</th>
                      <th>Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {igStories.map((s) => {
                      const m = s.metrics;
                      const posted =
                        s.posted_at === null
                          ? null
                          : shortDate(s.posted_at.slice(0, 10));
                      return (
                        <tr key={s.story_id}>
                          <td className="table-card-title">
                            {s.permalink !== null ? (
                              <a
                                href={s.permalink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {posted ?? "—"}
                              </a>
                            ) : (
                              posted ?? "—"
                            )}
                          </td>
                          {s.metrics_error !== null ? (
                            <td colSpan={7} className="muted">
                              metrics unavailable ({s.metrics_error})
                            </td>
                          ) : (
                            <>
                              <td data-label="Reach">{fmtInt(num(m["reach"]))}</td>
                              <td data-label="Views">{fmtInt(num(m["views"]))}</td>
                              <td data-label="Replies">{fmtInt(num(m["replies"]))}</td>
                              <td data-label="Shares">{fmtInt(num(m["shares"]))}</td>
                              <td data-label="Navigation">{fmtInt(num(m["navigation"]))}</td>
                              <td data-label="Follows">{fmtInt(num(m["follows"]))}</td>
                              <td data-label="Profile visits">{fmtInt(num(m["profile_visits"]))}</td>
                            </>
                          )}
                          <td data-label="Seen">{s.observations}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : null}
      </section>
    </div>
  );
}
